// Live totals + take-off sidebar. Pure presentation — receives `preview`
// from the backend `/quotes/preview` endpoint.
import React from "react";
import { Warning } from "@phosphor-icons/react";
import { POUND } from "../../lib/api";

function Cell({ label, value }) {
  return (
    <div className="bg-slate-50 p-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="font-bold mt-0.5">{value}</div>
    </div>
  );
}

export default function TotalsSidebar({ preview, warningsSummary, vatEnabled = true }) {
  const s = preview?.summary || {};
  const c = preview?.calculations || {};
  const f = c?.features_rollup || {};
  const total = s.total || 0;
  const subtotal = s.subtotal || 0;
  const warnTotal = (warningsSummary?.warning || 0) + (warningsSummary?.critical || 0);
  // Prefer the live preview value; fall back to the local toggle so the UI
  // updates the instant the user clicks the checkbox, before the debounced
  // preview returns.
  const vatOn = s.vat_enabled === undefined ? vatEnabled : s.vat_enabled !== false;

  return (
    <div className="card-elev p-5 sticky top-4" data-testid="live-totals">
      <div className="overline">Quote total</div>
      <div className="font-mono-data text-4xl font-bold tracking-tight text-[var(--rqp-orange)] mt-1">{POUND(total)}</div>
      <div className="text-xs text-slate-500 mt-1">inc. VAT</div>

      {warnTotal > 0 && (
        <div className="mt-3 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-amber-800 bg-amber-50 border border-amber-200 px-2 py-1 rounded-sm" data-testid="totals-warning-badge">
          <Warning size={12} weight="fill" /> {warnTotal} compliance issue{warnTotal === 1 ? "" : "s"}
        </div>
      )}

      <div className="border-t border-slate-200 mt-4 pt-4 text-sm space-y-2 font-mono-data">
        <div className="flex justify-between"><span className="text-slate-500 font-sans">Materials</span><span>{POUND(s.materials_total || 0)}</span></div>
        <div className="flex justify-between"><span className="text-slate-500 font-sans">Labour</span><span>{POUND(s.labour_total || 0)}</span></div>
        <div className="flex justify-between"><span className="text-slate-500 font-sans">Scaffolding</span><span>{POUND(s.scaffolding || 0)}</span></div>
        <div className="flex justify-between"><span className="text-slate-500 font-sans">Waste removal</span><span>{POUND(s.waste_removal || 0)}</span></div>
        <div className="flex justify-between"><span className="text-slate-500 font-sans">Overheads ({s.overheads_pct || 0}%)</span><span>{POUND(s.overheads || 0)}</span></div>
        <div className="flex justify-between"><span className="text-slate-500 font-sans">Profit ({s.profit_margin_pct || 0}%)</span><span>{POUND(s.profit || 0)}</span></div>
        <div className="flex justify-between border-t border-slate-200 pt-2"><span className="font-semibold font-sans">Subtotal</span><span className="font-bold">{POUND(subtotal)}</span></div>
        {!vatOn ? (
          <div className="flex justify-between" data-testid="vat-not-registered">
            <span className="text-slate-500 font-sans">VAT</span>
            <span className="text-[11px] uppercase tracking-wider font-bold">Not VAT registered</span>
          </div>
        ) : (
          <div className="flex justify-between" data-testid="vat-line">
            <span className="text-slate-500 font-sans">VAT ({s.vat_pct || 0}%)</span>
            <span>{POUND(s.vat || 0)}</span>
          </div>
        )}
      </div>

      <div className="border-t border-slate-200 mt-4 pt-4">
        <div className="overline mb-2">Take-off</div>
        <div className="grid grid-cols-2 gap-2 text-xs font-mono-data">
          <Cell label="Area" value={`${(c.roof_area_m2 || 0).toFixed(1)} m²`} />
          <Cell label="Sections" value={c.section_count || 0} />
          <Cell label="Battens" value={`${(c.battens_m || 0).toFixed(0)} m`} />
          <Cell label="Underlay" value={`${(c.underlay_m2 || 0).toFixed(0)} m²`} />
          <Cell label="Ridge" value={`${(c.ridge_length_m || 0).toFixed(1)} m`} />
          <Cell label="Hips" value={`${(c.hip_length_m || 0).toFixed(1)} m`} />
          <Cell label="Valleys" value={`${(c.valley_length_m || 0).toFixed(1)} m`} />
          <Cell label="Eaves" value={`${(c.eaves_length_m || 0).toFixed(1)} m`} />
          <Cell label="Verges" value={`${(c.verge_length_m || 0).toFixed(1)} m`} />
          <Cell label="Abutment" value={`${(c.abutment_length_m || 0).toFixed(1)} m`} />
        </div>
      </div>

      {(f.velux_count || f.dormer_count || f.chimney_count || f.solar_panels) > 0 && (
        <div className="border-t border-slate-200 mt-4 pt-4">
          <div className="overline mb-2">Features</div>
          <div className="grid grid-cols-2 gap-2 text-xs font-mono-data">
            {!!f.velux_count && <Cell label="Velux" value={f.velux_count} />}
            {!!f.dormer_count && <Cell label="Dormers" value={f.dormer_count} />}
            {!!f.chimney_count && <Cell label="Chimneys" value={f.chimney_count} />}
            {!!f.solar_panels && <Cell label="Solar PV" value={`${f.solar_panels} pcs · ${(f.solar_area_m2 || 0).toFixed(1)} m²`} />}
            {!!f.flashing_m && <Cell label="Flashing" value={`${f.flashing_m.toFixed(1)} m`} />}
          </div>
        </div>
      )}
    </div>
  );
}
